import { getOnlineMeetingProviderInfo } from './getOnlineMeetingProviderInfo';
import { ControlIcons } from 'owa-control-icons';
import loc from 'owa-localize';
import { onlineSkypeMeetingLocationLabel } from 'owa-locstrings/lib/strings/onlineskypemeetinglocationlabel.locstring.json';
import { onlineTeamsMeetingLocationLabel } from 'owa-locstrings/lib/strings/onlineteamsmeetinglocationlabel.locstring.json';
import type { OnlineMeetingProviderType } from './OnlineMeetingProviderType';
import type CalendarEvent from 'owa-calendar-types/lib/types/CalendarEvent';
import {
    joinSkypeMeetingButton,
    joinTeamsMeetingButton,
    joinUnknownProviderMeetingButton,
    joinOnlineMeetingCompactButton,
} from 'owa-locstrings/lib/strings/joinonlinemeetingbutton.locstring.json';
import { isValidOnlineMeeting } from './onlineMeetingBootHelper';

export function getIconFromProviderName(provider: OnlineMeetingProviderType) {
    switch (provider) {
        case 'SkypeForBusiness': {
            return ControlIcons.SkypeForBusinessLogo16;
        }
        case 'SkypeForConsumer': {
            return ControlIcons.SkypeLogo16;
        }
        case 'TeamsForBusiness': {
            return ControlIcons.TeamsLogo;
        }
    }

    return null;
}

/**
 * Gets the Join button label based on the provider name or a default label if the provider is missing
 * @param item - calendar event
 * @param isCompact true for Surface, Peek and Agenda
 * @returns the join button label
 */
export function getJoinOnlineMeetingLabel(item: CalendarEvent, isCompact?: boolean): string | null {
    if (!isValidOnlineMeeting(item)) {
        return null;
    }

    if (!item.OnlineMeetingProvider) {
        return isCompact
            ? loc(joinOnlineMeetingCompactButton)
            : loc(joinUnknownProviderMeetingButton);
    }

    return getJoinMeetingLabelFromProviderName(
        item.OnlineMeetingProvider as OnlineMeetingProviderType,
        isCompact
    );
}

/**
 * Gets the Join button label based on the provider name
 * @param provider of the online meeting
 * @param isCompact true for Surface, Peek and Agenda
 * @returns the join button label
 */
export function getJoinMeetingLabelFromProviderName(
    provider: OnlineMeetingProviderType,
    isCompact?: boolean
) {
    if (isCompact) {
        return loc(joinOnlineMeetingCompactButton);
    }

    switch (provider) {
        case 'SkypeForBusiness': {
            return loc(joinSkypeMeetingButton);
        }
        case 'SkypeForConsumer': {
            return loc(joinSkypeMeetingButton);
        }
        case 'TeamsForBusiness': {
            return loc(joinTeamsMeetingButton);
        }
        case 'Unknown': {
            return loc(joinUnknownProviderMeetingButton);
        }
    }

    return null;
}

/**
 * Gets the online provider label shown in the online providers drop down
 * @param provider of the online meeting
 * @returns the text used in the online provider dropdown
 */
export function getOnlineProviderLabel(provider: OnlineMeetingProviderType): string {
    let onlineProviderLabel;

    switch (provider) {
        case 'SkypeForBusiness':
        case 'SkypeForConsumer': {
            onlineProviderLabel = loc(onlineSkypeMeetingLocationLabel);
            break;
        }
        case 'TeamsForBusiness':
            onlineProviderLabel = loc(onlineTeamsMeetingLocationLabel);
            break;
    }

    return onlineProviderLabel;
}

/**
 * Gets whether a online provider type is a supported type by OWA
 * @param provider of the online meeting
 * @returns whether the provide is a supported type by OWA
 */
export function isSupportedOnlineMeetingProvider(provider: string): boolean {
    return (
        provider === 'SkypeForBusiness' ||
        provider === 'SkypeForConsumer' ||
        provider === 'TeamsForBusiness'
    );
}

export function getOnlineMeetingIconName(item: CalendarEvent): string {
    if (item.OnlineMeetingProvider) {
        return getIconFromProviderName(item.OnlineMeetingProvider as OnlineMeetingProviderType);
    }

    const providers = getOnlineMeetingProviderInfo(item);
    if (!providers) {
        return null;
    }

    if (providers.length >= 2) {
        return ControlIcons.Video;
    } else if (providers.length === 1 && isSupportedOnlineMeetingProvider(providers[0])) {
        return getIconFromProviderName(providers[0]);
    }

    return null;
}
